/**
 * @file Routing - typed wire contracts and binding helpers.
 *
 * One module covering the entire request/response and subscription surface:
 *
 * - **`Route` / `HttpRoute`** — typed HTTP route contracts (method, path,
 *   params, query, request, response schemas). Defined via `defineRoute(...)`
 *   / `defineHttpRoute(...)` and consumed by both the backend (serves the
 *   route) and the frontend (calls it with type-safe req/res).
 * - **`SubscriptionContract`** — typed WebSocket subscription contract
 *   (path, params, query, client-frame / server-frame schemas). Defined via
 *   `defineSubscription(...)`; the dispatcher validates inbound frames and
 *   closes with `WS_CLOSE_INVALID_FRAME` (4001) on failure.
 * - **`bindRoute` / `bindWebSocket`** — bind a contract to a handler,
 *   producing the `Bound*Record` the `CoreService.routes` / `subscriptions`
 *   getters return.
 * - **`ExtractPathParams<P>`** — type-level extraction of `:named` segments
 *   from a path literal; powers the compile-time check that a `params`
 *   schema covers every segment.
 * - **`interpolate`** — runtime substitution of `:named` segments with
 *   URL-encoded values.
 *
 * Everything here is data + factory functions: contracts are frozen plain
 * objects (so `defineRoute({ path: '/u/:id', ... })` preserves the literal
 * `'/u/:id'` for `ExtractPathParams`), no class identity, no `this`-state.
 *
 * @module
 */

import type { z } from 'zod'
import { CoreError } from './core_error.ts'

// ── ExtractPathParams (type-only) ─────────────────────────────────────────

/**
 * Extract `:named` segments from a URL path literal into a record of
 * `{ [name]: string }`. Paths with no `:name` segments yield
 * `Record<string, never>`.
 *
 * Caveat: when `P` is the wide `string` type (not a literal), this widens to
 * `Record<string, string>`. The `defineHttpRoute(...)` / `defineSubscription`
 * callsites use `const P extends string` to keep the literal narrow.
 *
 * @example
 * ```typescript
 * type A = ExtractPathParams<'/users/:id'>            // { id: string }
 * type B = ExtractPathParams<'/u/:id/posts/:postId'>  // { id: string; postId: string }
 * type C = ExtractPathParams<'/health'>               // Record<string, never>
 * ```
 */
export type ExtractPathParams<P extends string> = string extends P
  ? Record<string, string>
  : [PathParamNames<P>] extends [never]
    ? Record<string, never>
    : { [K in PathParamNames<P>]: string }

/**
 * Recursive helper: union of `:named` segment names from a path literal.
 * Mapping over the union (`{ [K in NameUnion]: string }`) produces a clean
 * object whose `keyof` is exactly the union — intersecting per-step would
 * widen `keyof` to `string` when the recursion bottomed out on a static tail.
 */
type PathParamNames<P extends string> =
  P extends `${string}:${infer Param}/${infer Rest}`
    ? Param | PathParamNames<`/${Rest}`>
    : P extends `${string}:${infer Param}`
      ? Param
      : never

/**
 * Compile-time constraint: when a `params` schema is provided, its key set
 * must cover every `:named` segment in `path`. Intersecting this with the
 * factory props produces an extra `_error` field whenever the constraint
 * fails, surfacing a clear TS error at the callsite.
 *
 * Key-only comparison (not type-level) so coerced schemas like
 * `z.coerce.number()` whose input is `unknown` still satisfy the constraint
 * as long as the right key is present.
 */
type _ParamsCompatible<P extends string, Params> = [
  keyof ExtractPathParams<P>,
] extends [keyof Params]
  ? unknown
  : {
      readonly _error: 'params schema must cover all :named path segments'
      readonly _path: P
      readonly _missing: Exclude<keyof ExtractPathParams<P>, keyof Params>
    }

// ── Route (the original Route shape) ──────────────────────────────────────

/** HTTP methods supported by routes. */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

/**
 * Route definition for type-safe API contracts.
 *
 * @property method - HTTP method.
 * @property path - URL path (e.g. `/api/health`).
 * @property params - Zod schema for `:named` path-param validation. When
 *   present, the backend `safeParse`s the extracted segments and responds
 *   400 on mismatch before the handler runs.
 * @property query - Zod schema for query-param validation; same loud-reject
 *   semantics as `params`.
 * @property request - Zod schema for request body (optional for GET/DELETE).
 * @property response - Zod schema for response body.
 * @property stream - True if this route returns a Server-Sent Events stream.
 * @property description - Human-readable description.
 */
export type Route<Req = unknown, Res = unknown> = {
  readonly method: HttpMethod
  readonly path: string
  readonly params?: z.ZodType<unknown>
  readonly query?: z.ZodType<unknown>
  readonly request?: z.ZodSchema<Req>
  readonly response: z.ZodSchema<Res>
  readonly stream?: boolean
  readonly description?: string
}

/** Props for `defineRoute(...)` — same as `Route` but with generic `Stream` for literal inference. */
export type RouteProps<
  Req = unknown,
  Res = unknown,
  Stream extends boolean | undefined = boolean | undefined,
> = {
  method: HttpMethod
  path: string
  params?: z.ZodType<unknown>
  query?: z.ZodType<unknown>
  request?: z.ZodSchema<Req>
  response: z.ZodSchema<Res>
  stream?: Stream
  description?: string
}

/**
 * Define a type-safe route. Identity-shaped factory that preserves literal
 * inference (especially on `stream`).
 */
export function defineRoute<
  Req,
  Res,
  const Stream extends boolean | undefined = undefined,
>(
  props: RouteProps<Req, Res, Stream>,
): Route<Req, Res> & {
  readonly stream: Stream
} {
  return Object.freeze(props) as Route<Req, Res> & { readonly stream: Stream }
}

/** Extract request type from a route. Returns `undefined` if no request schema. */
export type RouteRequest<R extends Route> =
  NonNullable<R['request']> extends z.ZodSchema<infer T> ? T : undefined

/** Extract response type from a route. */
export type RouteResponse<R extends Route> =
  R['response'] extends z.ZodSchema<infer T> ? T : never

// ── HttpRoute (typed params + query + per-status responses) ───────────────

/**
 * HTTP route shape with first-class path params, query params, and per-status
 * responses. Coexists with `Route`; migration is opt-in.
 *
 * @typeParam P - Path literal (carries `:named` segment info into types).
 * @typeParam Params - Inferred type of the params schema.
 * @typeParam Query - Inferred type of the query schema.
 * @typeParam Req - Inferred type of the request schema.
 * @typeParam ResMap - Map of status code → response Zod schema. The shorthand
 *   `response` prop on `HttpRouteProps` desugars to `{ 200: response }`.
 */
export type HttpRoute<
  P extends string = string,
  Params = unknown,
  Query = unknown,
  Req = unknown,
  ResMap extends Record<number, z.ZodType<unknown>> = Record<
    number,
    z.ZodType<unknown>
  >,
> = {
  readonly method: HttpMethod
  readonly path: P
  readonly params?: z.ZodType<Params>
  readonly query?: z.ZodType<Query>
  readonly request?: z.ZodType<Req>
  readonly responses: ResMap
  readonly stream?: boolean
  readonly description?: string
}

/**
 * Props for `defineHttpRoute(...)`. `response` is shorthand for `responses:
 * { 200: response }`; pick one (if both, `responses` wins). Both omitted
 * yields an empty responses map (legal for routes that only document
 * params/query).
 */
export type HttpRouteProps<
  P extends string,
  Params,
  Query,
  Req,
  Res,
  ResMap extends Record<number, z.ZodType<unknown>>,
> = {
  method: HttpMethod
  path: P
  params?: z.ZodType<Params>
  query?: z.ZodType<Query>
  request?: z.ZodType<Req>
  response?: z.ZodType<Res>
  responses?: ResMap
  stream?: boolean
  description?: string
}

/**
 * Define a type-safe HTTP route. Preserves literal inference on `path`,
 * `stream`, and `responses`'s status-code keys. The `Params` constraint
 * catches missing schema keys at compile time.
 *
 * @example
 * ```typescript
 * const GetUser = defineHttpRoute({
 *   method: 'GET',
 *   path: '/users/:id',
 *   params: z.object({ id: z.string().uuid() }),
 *   responses: { 200: UserSchema, 404: ErrorSchema },
 * })
 *
 * // 200-only shorthand:
 * const Health = defineHttpRoute({
 *   method: 'GET',
 *   path: '/api/health',
 *   response: HealthResponseSchema,  // → responses: { 200: ... }
 * })
 * ```
 */
export function defineHttpRoute<
  const P extends string,
  Params = ExtractPathParams<P>,
  Query = unknown,
  Req = unknown,
  Res = unknown,
  ResMap extends Record<number, z.ZodType<unknown>> = {
    200: z.ZodType<Res>
  },
>(
  props: HttpRouteProps<P, Params, Query, Req, Res, ResMap> &
    _ParamsCompatible<P, Params>,
): HttpRoute<P, Params, Query, Req, ResMap> {
  const responses =
    props.responses ??
    (props.response ? { 200: props.response } : ({} as Record<number, never>))

  return Object.freeze({
    method: props.method,
    path: props.path,
    params: props.params,
    query: props.query,
    request: props.request,
    responses,
    stream: props.stream,
    description: props.description,
  }) as HttpRoute<P, Params, Query, Req, ResMap>
}

/** Extract the response type for a given status code from an HttpRoute. */
export type HttpRouteResponse<R extends HttpRoute, S extends number = 200> =
  R['responses'] extends Record<S, z.ZodType<infer T>> ? T : never

/** Extract the params type from an HttpRoute (or `undefined` if absent). */
export type HttpRouteParams<R extends HttpRoute> =
  NonNullable<R['params']> extends z.ZodType<infer T> ? T : undefined

/** Extract the query type from an HttpRoute (or `undefined` if absent). */
export type HttpRouteQuery<R extends HttpRoute> =
  NonNullable<R['query']> extends z.ZodType<infer T> ? T : undefined

/** Extract the request type from an HttpRoute (or `undefined` if absent). */
export type HttpRouteRequest<R extends HttpRoute> =
  NonNullable<R['request']> extends z.ZodType<infer T> ? T : undefined

// ── SubscriptionContract (typed WebSocket wire contract) ──────────────────

/**
 * WebSocket close code emitted when a client frame fails `clientFrame`
 * validation server-side. Falls in the app-level 4xxx band so the
 * `WsSubscription` does not auto-reconnect — the typed client knows the
 * frame is malformed and should not retry blindly.
 */
export const WS_CLOSE_INVALID_FRAME = 4001

/**
 * Wire contract for a typed WebSocket subscription. Produced by
 * `defineSubscription(...)`.
 *
 * @typeParam P - Path literal (carries `:named` segment info into types).
 * @typeParam Params - Inferred output type of the params schema.
 * @typeParam Query - Inferred output type of the query schema.
 * @typeParam ClientFrame - Inferred output type of the client-frame schema —
 *   a Zod discriminated union on `type`.
 * @typeParam ServerFrame - Inferred output type of the server-frame schema —
 *   a Zod discriminated union on `type`.
 */
export type SubscriptionContract<
  P extends string = string,
  Params = unknown,
  Query = unknown,
  ClientFrame = unknown,
  ServerFrame = unknown,
> = {
  readonly path: P
  readonly params?: z.ZodType<Params>
  readonly query?: z.ZodType<Query>
  readonly clientFrame: z.ZodType<ClientFrame>
  readonly serverFrame: z.ZodType<ServerFrame>
  readonly description?: string
}

/** Props for `defineSubscription(...)`. */
export type SubscriptionProps<P extends string, Params, Query, C, S> = {
  path: P
  params?: z.ZodType<Params>
  query?: z.ZodType<Query>
  clientFrame: z.ZodType<C>
  serverFrame: z.ZodType<S>
  description?: string
}

/**
 * Define a typed WebSocket subscription contract. Both frame schemas should
 * be Zod discriminated unions on a `type` field; the dispatcher and the
 * typed client both rely on the discriminator for cheap routing. Path-param
 * keys (when `params` is present) must cover every `:named` segment in
 * `path` — enforced at compile time.
 *
 * @example
 * ```typescript
 * const ChatRoom = defineSubscription({
 *   path: '/rooms/:id/ws',
 *   params: z.object({ id: z.string() }),
 *   clientFrame: z.discriminatedUnion('type', [
 *     z.object({ type: z.literal('say'), text: z.string() }),
 *     z.object({ type: z.literal('leave') }),
 *   ]),
 *   serverFrame: z.discriminatedUnion('type', [
 *     z.object({ type: z.literal('joined'), userId: z.string() }),
 *     z.object({ type: z.literal('said'), userId: z.string(), text: z.string() }),
 *   ]),
 * })
 * ```
 */
export function defineSubscription<
  const P extends string,
  Params = ExtractPathParams<P>,
  Query = unknown,
  C = unknown,
  S = unknown,
>(
  props: SubscriptionProps<P, Params, Query, C, S> &
    _ParamsCompatible<P, Params>,
): SubscriptionContract<P, Params, Query, C, S> {
  return Object.freeze({
    path: props.path,
    params: props.params,
    query: props.query,
    clientFrame: props.clientFrame,
    serverFrame: props.serverFrame,
    description: props.description,
  }) as SubscriptionContract<P, Params, Query, C, S>
}

/**
 * Result of validating a single inbound frame against a schema. Tagged
 * union so the caller can branch on `ok` and propagate either the parsed
 * value or the validation issues onward (e.g. into a `ws.close(4001, …)`
 * call server-side).
 */
export type FrameValidationResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly issues: ReadonlyArray<z.ZodIssue> }

/**
 * Validate an inbound frame against a Zod schema. Doesn't side-effect —
 * the caller decides what to do on failure (server-side: close 4001;
 * client-side: drop + log via `onError`).
 */
export function validateFrame<T>(
  schema: z.ZodType<T>,
  raw: unknown,
): FrameValidationResult<T> {
  const parsed = schema.safeParse(raw)
  if (parsed.success) return { ok: true, value: parsed.data }
  return { ok: false, issues: parsed.error.issues }
}

// ── bindRoute / bindWebSocket (contract + handler → BoundRecord) ──────────

/**
 * Structural shape `bindRoute` returns. Mirrors `core/backend/types.ts`'s
 * `BoundRoute` field-by-field so existing consumers that expect that type
 * are happy without an explicit `import type`.
 */
export type BoundRouteRecord = {
  method: HttpMethod
  path: string
  handler: (req: Request, ctx?: unknown) => Response | Promise<Response>
  description?: string
  params?: z.ZodType<unknown>
  query?: z.ZodType<unknown>
}

/** Handler shape `bindRoute` accepts. */
export type BoundRouteHandler = BoundRouteRecord['handler']

/**
 * Structural shape `bindWebSocket` returns. Mirrors `BoundWebSocket` /
 * `BoundSubscription` in `core/backend/types.ts`.
 */
export type BoundWebSocketRecord = {
  path: string
  handler: (
    connection: unknown,
    url: URL,
    params: Record<string, string>,
    ctx: unknown,
  ) => void
  description?: string
  clientFrame?: z.ZodType<unknown>
  serverFrame?: z.ZodType<unknown>
}

/** Handler shape `bindWebSocket` accepts. */
export type BoundWebSocketHandler = BoundWebSocketRecord['handler']

/**
 * Bind a shared `Route` (or `HttpRoute`) contract to a handler function.
 * The returned record carries the route's `method`, `path`, `description`,
 * and (for `HttpRoute`s) optional `params` / `query` Zod schemas. The wire
 * path is authored on the contract — no prefix manipulation.
 */
export function bindRoute<R extends Route | HttpRoute>(
  route: R,
  handler: BoundRouteHandler,
): BoundRouteRecord {
  return {
    method: route.method,
    path: route.path,
    handler,
    description: route.description,
    params: 'params' in route ? route.params : undefined,
    query: 'query' in route ? route.query : undefined,
  }
}

/**
 * Bind a typed `SubscriptionContract` to a WebSocket upgrade handler.
 * Forwards the contract's `clientFrame` / `serverFrame` schemas onto the
 * returned record so the dispatcher can validate inbound frames + close
 * with `WS_CLOSE_INVALID_FRAME` (4001) on failure.
 *
 * The handler signature accepts the framework's `WsConnection` shape; types
 * stay `unknown` here so this module remains free of `core/backend/`
 * imports.
 */
export function bindWebSocket<C, S>(
  contract: SubscriptionContract<string, unknown, unknown, C, S>,
  handler: BoundWebSocketHandler,
): BoundWebSocketRecord {
  return {
    path: contract.path,
    handler,
    description: contract.description,
    clientFrame: contract.clientFrame as z.ZodType<unknown>,
    serverFrame: contract.serverFrame as z.ZodType<unknown>,
  }
}

// ── interpolate (runtime substitution of :named segments) ─────────────────

/**
 * Replace `:name` segments in a route path with URL-encoded values from
 * `params`. A path with no `:name` segments returns unchanged. Throws
 * `PATH_PARAM_MISSING` when a placeholder has no matching value — surfaces
 * typos in caller code as a `CoreError` instead of a wire 404.
 */
export function interpolate(
  path: string,
  params: Record<string, string>,
): string {
  return path.replace(/:([a-zA-Z][a-zA-Z0-9_]*)/g, (_, name: string) => {
    const value = params[name]
    if (value === undefined) {
      throw new CoreError({
        code: 'PATH_PARAM_MISSING',
        message: `path param :${name} missing`,
        metadata: { path, params },
      })
    }
    return encodeURIComponent(value)
  })
}
